import axios from "axios"
import getEnv from "../utils/env"

// Get the configured base URL
let envBaseURL = getEnv("VUE_APP_API_BASE_URL")

// Determine if we're in development mode
const isDevelopment = process.env.NODE_ENV !== 'production' || window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'

// Force use proxy in development (empty baseURL) - ignore window.configs
// In production, use configured URL or default to 8081
let apiBaseURL = ''
if (isDevelopment) {
	// Development: ALWAYS use proxy (empty string) - ignore any configured URL
	// This ensures requests go through vue.config.js proxy to port 8081
	apiBaseURL = ''
	console.log("[API] Development mode detected - using proxy for API calls")
	console.log("[API] Ignoring configured URL:", envBaseURL)
} else {
	// Production: use configured URL or default
	apiBaseURL = envBaseURL || 'http://localhost:8081'
	// Fix port if wrong
	if (apiBaseURL && apiBaseURL.includes(':3000')) {
		apiBaseURL = apiBaseURL.replace(':3000', ':8081')
		console.warn("[API] Fixed port from 3000 to 8081")
	}
}

// Remove trailing slash to avoid double slashes
if (apiBaseURL && apiBaseURL.endsWith('/')) {
	apiBaseURL = apiBaseURL.slice(0, -1)
}

const api = axios.create({
	baseURL: apiBaseURL,
	headers: {
		Accept: "application/json",
		"Content-Type": "application/json",
	},
	// Prevent axios from following redirects automatically
	// This prevents issues when backend redirects to /login
	maxRedirects: 0,
	validateStatus: function (status) {
		// Accept 2xx and 3xx status codes without throwing
		// We'll handle redirects manually in the interceptor
		return status >= 200 && status < 400;
	}
})

// Ensure baseURL is correct (in case something tries to override it)
if (isDevelopment && api.defaults.baseURL !== '') {
	console.warn("[API] baseURL was overridden! Resetting to empty string for proxy")
	api.defaults.baseURL = ''
}

// Add request interceptor for debugging and URL fixing
api.interceptors.request.use(
	config => {
		// Force empty baseURL in development to use proxy
		if (isDevelopment) {
			config.baseURL = ''
		}
		
		// Ensure URL is relative (starts with /) to use proxy
		if (config.url && config.url.startsWith('http')) {
			// Extract path from absolute URL
			try {
				const urlObj = new URL(config.url)
				config.url = urlObj.pathname + urlObj.search
			} catch (e) {
				console.error("[API] Failed to parse URL:", e)
			}
		}
		
		// Remove any upgrade headers that might cause 426 errors
		if (config.headers) {
			delete config.headers.upgrade
			delete config.headers.Upgrade
			delete config.headers.connection
			delete config.headers.Connection
		}
		
		// Strip cookies to prevent 431 errors (Request Header Fields Too Large)
		// The API doesn't use cookies for authentication, so we can safely remove them
		if (config.headers && config.headers.Cookie) {
			const cookieSize = config.headers.Cookie.length
			if (cookieSize > 1024) {
				console.warn(`[API] Large cookie header detected (${cookieSize} bytes), removing to prevent 431 error`)
			}
			delete config.headers.Cookie
			delete config.headers.cookie
		}
		// Also remove cookies from the withCredentials setting
		config.withCredentials = false
		
		// Ensure URL starts with /api if it's an API call
		if (config.url && !config.url.startsWith('/api') && !config.url.startsWith('http')) {
			console.warn("[API] URL doesn't start with /api, prepending:", config.url)
		}
		
		//const fullURL = (config.baseURL || '') + config.url
		return config
	},
	error => {
		console.error("[API] Request error:", error)
		return Promise.reject(error)
	}
)

// Add response interceptor for debugging and error handling
api.interceptors.response.use(
	response => {
		// Handle redirects manually - don't let axios follow them automatically
		if (response.status === 301 || response.status === 302 || response.status === 307 || response.status === 308) {
			const location = response.headers.location || response.headers.Location
			if (location) {
				// If redirecting to /login, it means authentication is required
				if (location.includes('/login') || location === '/login') {
					const authError = new Error("Backend requires authentication. Please restart the backend server to apply the public blocks endpoint changes.")
					authError.status = 401
					authError.code = 401
					authError.response = response
					console.error("[API] Backend redirected to /login - authentication required. Backend needs restart.")
					return Promise.reject(authError)
				}
			}
		}
		console.log("[API] Response:", response.status, response.config.url)
		return response
	},
	error => {
		console.error("[API] Response error:", error.config?.url, error.message)
		
		// Handle 426 Upgrade Required error specifically
		if (error.response && error.response.status === 426) {
			const upgradeError = new Error("Protocol upgrade required. Please ensure you're using HTTP (not WebSocket) for API requests.")
			upgradeError.status = 426
			upgradeError.code = 426
			upgradeError.message = "Protocol upgrade required. The server requires HTTP protocol. Please check your connection settings."
			console.error("[API] 426 Upgrade Required error detected")
			return Promise.reject(upgradeError)
		}
		
		// Handle 431 Request Header Fields Too Large error
		if (error.response && error.response.status === 431) {
			const headerError = new Error("Request headers are too large. Please clear your browser cookies and try again.")
			headerError.status = 431
			headerError.code = 431
			headerError.message = "Request headers are too large. Please clear your browser cookies and try again."
			console.error("[API] 431 Request Header Fields Too Large error detected")
			return Promise.reject(headerError)
		}
		
		// Handle redirects to /login (authentication required)
		if (error.response && (error.response.status === 302 || error.response.status === 301)) {
			const location = error.response.headers.location
			if (location && location.includes('/login')) {
				const authError = new Error("Authentication required. The backend may need to be restarted after making blocks endpoint public.")
				authError.status = 401
				authError.code = 401
				authError.message = "Authentication required. Please ensure the backend server has been restarted."
				console.error("[API] Redirected to /login - authentication issue")
				return Promise.reject(authError)
			}
		}
		
		// Handle 404 errors that might be due to redirects
		if (error.response && error.response.status === 404) {
			const responseUrl = error.config?.url || error.config?.baseURL || ''
			const requestUrl = error.request?.responseURL || ''
			if (responseUrl.includes('/login') || requestUrl.includes('/login')) {
				const authError = new Error("Backend redirected to /login. Please restart the backend server to apply authentication changes.")
				authError.status = 401
				authError.code = 401
				console.error("[API] 404 on /login - backend authentication issue. Backend needs restart.")
				return Promise.reject(authError)
			}
		}
		
		// Handle cases where request URL contains /login (redirect was followed incorrectly)
		if (error.config && (
			(error.config.url && error.config.url.includes('/login')) ||
			(error.request && error.request.responseURL && error.request.responseURL.includes('/login'))
		)) {
			const authError = new Error("Backend requires authentication. Please restart the backend server - the blocks endpoint should be public.")
			authError.status = 401
			authError.code = 401
			console.error("[API] Request ended up at /login - backend needs restart to apply public blocks endpoint")
			return Promise.reject(authError)
		}
		
		// Extract error message from response if available
		if (error.response && error.response.data) {
			if (error.response.data.message) {
				error.message = error.response.data.message
			} else if (error.response.data.error && Array.isArray(error.response.data.error) && error.response.data.error.length > 0) {
				error.message = error.response.data.error[0].message || error.message
			}
		}
		
		return Promise.reject(error)
	}
)

export default api